import React from "react";
import { useNavigate } from "react-router-dom";
import { useOrder } from "../OrderContext";
import { getLang, t } from "../i18n";
import { PRODUCT_DEFS, customMix as ZERO_MIX, type CustomMix } from '../productDefs';
import Header from "../components/Header";
import ShakerLoader from "../components/ShakerLoader";
import BarScene from "../components/BarScene";
import ProductIcon from "../components/ProductIcon";
import { postFinalizeOrThrow, postRecommendOrThrow } from "./aiPick/api";
import { shouldUseCompactCaptureUpload } from "./aiPick/camera";
import { productsForPreference, type DrinkPreference } from "./aiPick/catalog";
import type { AiPickStage, FinalRecommendationResponse, Product, RecommendationResponse } from "./aiPick/types";
import { getHourForTimezone, sleep, weatherCodeToBucket } from "./aiPick/utils";
import {
  fetchMachineProducts,
  getMachineID,
  isMixedProduct,
  type MachineProduct,
} from "../api";

// Constants previously in config.ts (TF-specific ones removed)
const ML_API_URL = process.env.REACT_APP_FACE_DETECTION_URL ?? '';
const BURST_FRAME_COUNT = 5;
const BURST_FRAME_DELAY_MS = 100;
const COMPACT_UPLOAD_FRAME_COUNT = 2;
const COMPACT_UPLOAD_QUALITY = 0.7;
const DEFAULT_UPLOAD_QUALITY = 0.9;
const INTRO_MESSAGE_DELAY_MS = 2500;
const PREPARING_DELAY_MS = 1500;
const FACE_POLL_MS = 200;
const STILL_MOVE_THRESHOLD = 0.03;
const STILL_MS_TO_START = 1500;
const STILL_SIZE_THRESHOLD = 0.05;
const DEFAULT_FALLBACK_EMOTION = 'neutral';
const EMOTION_MODEL_KEY = 'mediapipe';
const CAPTURE_COUNTDOWN_SEC = 3;
const AI_POUR_ML = 200;

type FaceDetectorOptions = { fastMode?: boolean; maxDetections?: number };
type DetectedFace = { boundingBox: DOMRectReadOnly };
type FaceDetectorCtor = new (opts?: FaceDetectorOptions) => {
  detect(source: HTMLVideoElement | HTMLCanvasElement): Promise<DetectedFace[]>;
};

const AiPickPage = () => {
  const navigate = useNavigate();
  const { resetOrder, updateOrder, order } = useOrder();

  const products = React.useMemo(
    () =>
      PRODUCT_DEFS.map((p) => ({
        ...p,
        name: t.productNames[p.id],
        desc: t.productDescs[p.id],
      })) as Product[],
    [],
  );

  // This machine's real catalog (UUID product_ids from the backend). The AI
  // service ranks whatever menu we send it, so recommendations must be built
  // from these, not from the static PRODUCT_DEFS demo catalog — that one is
  // kept only as a fallback when no machine ID is set (demo mode).
  const [machineProducts, setMachineProducts] = React.useState<MachineProduct[]>([]);
  const [machineCatalogStatus, setMachineCatalogStatus] = React.useState<"loading" | "ready" | "error">("loading");
  const machineProductsPromiseRef = React.useRef<Promise<MachineProduct[]> | null>(null);
  const isDemoMode = React.useMemo(() => !getMachineID(), []);
  // Request payloads must AWAIT this rather than read state — a scan that
  // finishes before the fetch resolves would otherwise silently fall back to
  // the demo slug catalog and recommend drinks this machine doesn't carry.
  const loadMachineProducts = React.useCallback((): Promise<MachineProduct[]> => {
    if (!machineProductsPromiseRef.current) {
      setMachineCatalogStatus("loading");
      machineProductsPromiseRef.current = fetchMachineProducts()
        .then((all) => {
          setMachineProducts(all);
          setMachineCatalogStatus("ready");
          return all;
        })
        .catch(() => {
          machineProductsPromiseRef.current = null; // retry on next request
          setMachineCatalogStatus("error");
          return [] as MachineProduct[];
        });
    }
    return machineProductsPromiseRef.current;
  }, []);
  React.useEffect(() => { void loadMachineProducts(); }, [loadMachineProducts]);

  const machineProductName = React.useCallback(
    (p: MachineProduct) =>
      t.machineProductNames[p.name] ?? t.productNames[p.product_id] ?? p.name,
    [],
  );

  const [cameraReady, setCameraReady] = React.useState(false);
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [weather, setWeather] = React.useState("clear");
  const [temperatureC, setTemperatureC] = React.useState(22);
  const [hour, setHour] = React.useState<number>(new Date().getHours());
  const [loading, setLoading] = React.useState(false);
  const [finalizing, setFinalizing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<RecommendationResponse | null>(null);
  const [finalRecommendation, setFinalRecommendation] = React.useState<FinalRecommendationResponse | null>(null);
  const [uiStage, setUiStage] = React.useState<AiPickStage>("intro");
  const [introCanContinue, setIntroCanContinue] = React.useState(false);
  const [drinkPreference, setDrinkPreference] = React.useState<DrinkPreference | null>(null);
  const [memoryOptIn] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("rushMemoryOptIn") === "true";
  });
  const [countdownSec, setCountdownSec] = React.useState<number | null>(null);
  const [faceDetected, setFaceDetected] = React.useState(false);
  const [holdingStill, setHoldingStill] = React.useState(false);
  const [faceDetectionSupported, setFaceDetectionSupported] = React.useState<boolean | null>(null);
  const [cameraIntrinsic, setCameraIntrinsic] = React.useState<{ w: number; h: number } | null>(null);

  const alcoholicProducts = React.useMemo(
    () => productsForPreference(machineProducts, "alcoholic"),
    [machineProducts],
  );
  const nonAlcoholicProducts = React.useMemo(
    () => productsForPreference(machineProducts, "non-alcoholic"),
    [machineProducts],
  );
  const knownUnderage = order.user?.age !== undefined && order.user.age < 21;
  const machineCatalogLoading = machineCatalogStatus === "loading";
  const hasAlcoholicProducts =
    !machineCatalogLoading && !knownUnderage && alcoholicProducts.length > 0;
  const hasNonAlcoholicProducts =
    !machineCatalogLoading &&
    (nonAlcoholicProducts.length > 0 || (isDemoMode && products.length > 0));
  const allowAlcoholic = drinkPreference === "alcoholic" && !knownUnderage;
  const eligibleMachineProducts = React.useMemo(
    () =>
      drinkPreference === "alcoholic"
        ? alcoholicProducts
        : drinkPreference === "non-alcoholic"
          ? nonAlcoholicProducts
          : [],
    [alcoholicProducts, drinkPreference, nonAlcoholicProducts],
  );
  const eligibleProductIds = React.useMemo(() => {
    if (!drinkPreference) return new Set<string>();
    if (machineProducts.length > 0) {
      return new Set(eligibleMachineProducts.map((product) => product.product_id));
    }
    return isDemoMode && drinkPreference === "non-alcoholic"
      ? new Set(products.map((product) => product.id))
      : new Set<string>();
  }, [drinkPreference, eligibleMachineProducts, isDemoMode, machineProducts.length, products]);

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const previewContainerRef = React.useRef<HTMLDivElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const countdownIntervalRef = React.useRef<number | null>(null);
  const stillMsAccumRef = React.useRef(0);
  const prevNormCenterRef = React.useRef<{ nx: number; ny: number } | null>(null);
  const prevFillHRef = React.useRef<number | null>(null);
  const countdownStartedRef = React.useRef(false);
  const analyzeInFlightRef = React.useRef(false);
  const loadingRef = React.useRef(loading);
  const resultRef = React.useRef(result);
  const errorRef = React.useRef<string | null>(null);
  const cameraErrorRef = React.useRef<string | null>(null);

  React.useEffect(() => { loadingRef.current = loading; }, [loading]);
  React.useEffect(() => { resultRef.current = result; }, [result]);
  React.useEffect(() => { errorRef.current = error; }, [error]);
  React.useEffect(() => { cameraErrorRef.current = cameraError; }, [cameraError]);

  React.useEffect(() => {
    if (uiStage !== "intro") return undefined;
    setIntroCanContinue(false);
    const timeoutId = window.setTimeout(() => setIntroCanContinue(true), INTRO_MESSAGE_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [uiStage]);

  const clearCountdownInterval = React.useCallback(() => {
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    countdownStartedRef.current = false;
    setCountdownSec(null);
  }, []);

  React.useEffect(() => {
    if (error || cameraError) {
      clearCountdownInterval();
      stillMsAccumRef.current = 0;
      setHoldingStill(false);
    }
  }, [error, cameraError, clearCountdownInterval]);

  const handleBack = () => navigate("/ai-pick-menu");

  React.useEffect(() => {
    setHour(new Date().getHours());
    const fetchWeather = async (lat: number, lon: number) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&timezone=auto`,
        );
        const payload = await res.json();
        const current = payload?.current;
        if (!current) return;
        if (typeof payload?.timezone === "string") {
          const h = getHourForTimezone(payload.timezone);
          if (h !== null) setHour(h);
        }
        if (typeof current.temperature_2m === "number") setTemperatureC(Math.round(current.temperature_2m));
        if (typeof current.weather_code === "number") setWeather(weatherCodeToBucket(current.weather_code));
      } catch { /* use defaults */ }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => void fetchWeather(pos.coords.latitude, pos.coords.longitude),
        () => { /* use defaults */ },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 600000 },
      );
    }
  }, []);

  const stopCamera = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
    setCameraIntrinsic(null);
  }, []);

  React.useEffect(
    () => () => { stopCamera(); clearCountdownInterval(); },
    [stopCamera, clearCountdownInterval],
  );

  const handleAnalyzeRef = React.useRef<() => Promise<void>>(async () => {});

  const armCaptureCountdown = React.useCallback(() => {
    if (countdownIntervalRef.current !== null || analyzeInFlightRef.current) return;
    if (errorRef.current || cameraErrorRef.current) return;
    countdownStartedRef.current = true;
    let step = CAPTURE_COUNTDOWN_SEC;
    setCountdownSec(step);
    countdownIntervalRef.current = window.setInterval(() => {
      if (errorRef.current || cameraErrorRef.current) { clearCountdownInterval(); return; }
      step -= 1;
      if (step <= 0) {
        if (countdownIntervalRef.current !== null) {
          window.clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        countdownStartedRef.current = false;
        setCountdownSec(null);
        stillMsAccumRef.current = 0;
        if (!errorRef.current && !cameraErrorRef.current) void handleAnalyzeRef.current();
        return;
      }
      setCountdownSec(step);
    }, 1000);
  }, [clearCountdownInterval]);

  /** Face detection via native browser FaceDetector API (no TF). Falls back to timer if unsupported. */
  React.useEffect(() => {
    if (!cameraReady) {
      setFaceDetected(false);
      setHoldingStill(false);
      stillMsAccumRef.current = 0;
      prevNormCenterRef.current = null;
      prevFillHRef.current = null;
      clearCountdownInterval();
      return;
    }

    let cancelled = false;
    let intervalId: number | null = null;

    const stopInterval = () => {
      if (intervalId !== null) { window.clearInterval(intervalId); intervalId = null; }
    };

    type BBox = { x: number; y: number; width: number; height: number };

    const breakStillness = () => {
      stillMsAccumRef.current = 0;
      prevNormCenterRef.current = null;
      prevFillHRef.current = null;
      setHoldingStill(false);
      if (countdownStartedRef.current) clearCountdownInterval();
    };

    const startPolling = (detect: () => Promise<BBox | null>) => {
      intervalId = window.setInterval(async () => {
        if (cancelled) return;
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) { setFaceDetected(false); breakStillness(); return; }

        try {
          const bbox = await detect();
          if (!bbox) { setFaceDetected(false); breakStillness(); return; }

          const vw = video.videoWidth;
          const vh = video.videoHeight;
          const nx = (bbox.x + bbox.width / 2) / vw;
          const ny = (bbox.y + bbox.height / 2) / vh;
          const fillH = bbox.height / vh;

          let stable = true;
          const prev = prevNormCenterRef.current;
          if (prev && Math.hypot(nx - prev.nx, ny - prev.ny) > STILL_MOVE_THRESHOLD) stable = false;
          const prevFh = prevFillHRef.current;
          if (prevFh !== null && Math.abs(fillH - prevFh) > STILL_SIZE_THRESHOLD) stable = false;

          prevNormCenterRef.current = { nx, ny };
          prevFillHRef.current = fillH;

          if (!stable) {
            stillMsAccumRef.current = 0;
            setHoldingStill(false);
            if (countdownStartedRef.current) clearCountdownInterval();
          } else {
            stillMsAccumRef.current += FACE_POLL_MS;
            setHoldingStill(stillMsAccumRef.current >= STILL_MS_TO_START);
          }

          setFaceDetected(true);

          if (
            stable &&
            stillMsAccumRef.current >= STILL_MS_TO_START &&
            !countdownStartedRef.current &&
            !analyzeInFlightRef.current &&
            !loadingRef.current &&
            !resultRef.current &&
            !errorRef.current &&
            !cameraErrorRef.current
          ) {
            armCaptureCountdown();
          }
        } catch {
          setFaceDetected(false);
          breakStillness();
        }
      }, FACE_POLL_MS);
    };

    const FaceDetectorClass = (window as unknown as { FaceDetector?: FaceDetectorCtor }).FaceDetector;
    if (FaceDetectorClass) {
      try {
        const detector = new FaceDetectorClass({ fastMode: true, maxDetections: 1 });
        if (!cancelled) {
          setFaceDetectionSupported(true);
          startPolling(async () => {
            const video = videoRef.current;
            if (!video) return null;
            const faces = await detector.detect(video);
            const face = faces[0];
            if (!face) return null;
            const b = face.boundingBox;
            return { x: b.x, y: b.y, width: b.width, height: b.height };
          });
        }
      } catch {
        setFaceDetectionSupported(false);
      }
    } else {
      setFaceDetectionSupported(false);
    }

    return () => { cancelled = true; stopInterval(); };
  }, [cameraReady, clearCountdownInterval, armCaptureCountdown]);

  const handleAnalyze = React.useCallback(async () => {
    if (analyzeInFlightRef.current) return;
    analyzeInFlightRef.current = true;
    setLoading(true);
    setError(null);
    setResult(null);
    setFinalRecommendation(null);

    try {
      if (!drinkPreference) {
        throw new Error(t.aiPickNoMatchingDrinks);
      }
      const formData = new FormData();
      const compactUpload = shouldUseCompactCaptureUpload();
      const frameCount = compactUpload ? COMPACT_UPLOAD_FRAME_COUNT : BURST_FRAME_COUNT;
      const uploadQuality = compactUpload ? COMPACT_UPLOAD_QUALITY : DEFAULT_UPLOAD_QUALITY;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && cameraReady && video.videoWidth && video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          for (let i = 0; i < frameCount; i += 1) {
            ctx.drawImage(video, 0, 0);
            const blob = await new Promise<Blob | null>((resolve) =>
              canvas.toBlob(resolve, "image/jpeg", uploadQuality),
            );
            if (blob) {
              formData.append("image", new File([blob], `ai-pick-${Date.now()}-${i}.jpg`, { type: "image/jpeg" }));
            }
            if (i < frameCount - 1) await sleep(BURST_FRAME_DELAY_MS);
          }
        }
      }

      const userAge = order.user?.age;
      const userGender = order.user?.gender ?? 'unknown';

      formData.append("hour", String(hour));
      formData.append("weather", weather);
      formData.append("temperature_c", String(temperatureC));
      if (userAge !== undefined) formData.append("age", String(userAge));
      formData.append("gender", userGender);
      formData.append("emotion_model", EMOTION_MODEL_KEY);
      formData.append("manual_expression", DEFAULT_FALLBACK_EMOTION);
      formData.append("allow_alcoholic", allowAlcoholic ? "true" : "false");
      formData.append("memory_opt_in", memoryOptIn ? "true" : "false");
      const allMachineProducts = await loadMachineProducts();
      const eligible = productsForPreference(allMachineProducts, drinkPreference);
      if (allMachineProducts.length > 0) {
        if (eligible.length === 0) throw new Error(t.aiPickNoMatchingDrinks);
        // Send only the category the guest selected. The backend cannot rank a
        // product that is absent from this inventory payload.
        formData.append("inventory", JSON.stringify(eligible));
      } else if (isDemoMode && drinkPreference === "non-alcoholic") {
        // The static demo catalog is explicitly alcohol-free.
        products.forEach((product) => formData.append("machine_menu", product.id));
      } else {
        throw new Error(t.aiPickNoMatchingDrinks);
      }

      const payload = await postRecommendOrThrow(ML_API_URL, formData);
      setResult(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Recommendation request failed.");
    } finally {
      setLoading(false);
      analyzeInFlightRef.current = false;
    }
  }, [allowAlcoholic, cameraReady, drinkPreference, hour, isDemoMode, loadMachineProducts, memoryOptIn, order.user, products, weather, temperatureC]);

  React.useEffect(() => { handleAnalyzeRef.current = handleAnalyze; }, [handleAnalyze]);

  const startCamera = React.useCallback(async () => {
    setCameraError(null);
    setError(null);
    setCameraReady(false);
    setCameraIntrinsic(null);

    const attachStream = async (stream: MediaStream) => {
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error("Camera preview element is unavailable.");

      await new Promise<void>((resolve, reject) => {
        let settled = false;
        let timeoutId: number | null = null;
        let pollId: number | null = null;

        const cleanup = () => {
          if (timeoutId !== null) window.clearTimeout(timeoutId);
          if (pollId !== null) window.clearInterval(pollId);
          timeoutId = null; pollId = null;
          video.removeEventListener("loadedmetadata", bump);
          video.removeEventListener("loadeddata", bump);
          video.removeEventListener("canplay", bump);
          video.removeEventListener("playing", bump);
          video.removeEventListener("error", onVideoError);
        };

        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true; cleanup(); fn();
        };

        const tryPlay = async () => {
          if (settled || !video.videoWidth || !video.videoHeight) return;
          try { await video.play(); settle(() => resolve()); }
          catch (e) { settle(() => reject(e instanceof Error ? e : new Error("Camera playback failed."))); }
        };

        const bump = () => { void tryPlay(); };
        const onVideoError = () => settle(() => reject(new Error("Camera preview could not start.")));

        video.addEventListener("loadedmetadata", bump);
        video.addEventListener("loadeddata", bump);
        video.addEventListener("canplay", bump);
        video.addEventListener("playing", bump);
        video.addEventListener("error", onVideoError);

        video.srcObject = stream;
        void tryPlay();
        requestAnimationFrame(() => { void tryPlay(); });
        pollId = window.setInterval(() => { void tryPlay(); }, 200);
        timeoutId = window.setTimeout(
          () => settle(() => reject(new Error("Camera stream started but no frames arrived."))),
          15000,
        );
      });
    };

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("This browser does not support live camera capture.");
      }
      stopCamera();

      let stream: MediaStream | null = null;
      // Phone web app: always use the front (user-facing) camera. Try an exact
      // facingMode first so we never grab the rear camera, then fall back to a
      // soft preference and finally any available camera.
      const attempts: MediaStreamConstraints[] = [
        { video: { facingMode: { exact: "user" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
        { video: { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
        { video: true, audio: false },
      ];

      let lastErr: unknown = null;
      for (const constraints of attempts) {
        try { stream = await navigator.mediaDevices.getUserMedia(constraints); break; }
        catch (e) { lastErr = e; }
      }
      if (!stream) throw lastErr instanceof Error ? lastErr : new Error("Camera access failed.");

      await attachStream(stream);
      const video = videoRef.current;
      if (video?.videoWidth && video?.videoHeight) setCameraIntrinsic({ w: video.videoWidth, h: video.videoHeight });
      setCameraReady(true);
      setResult(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Camera access failed.";
      setCameraError(
        message.toLowerCase().includes("permission")
          ? "Camera permission was denied. Please allow camera access in the browser."
          : message,
      );
      stopCamera();
    }
  }, [stopCamera]);

  React.useEffect(() => {
    if (uiStage === "scan") {
      if (!cameraReady && !loading && !result && !finalRecommendation) void startCamera();
      return undefined;
    }
    clearCountdownInterval();
    stopCamera();
    return undefined;
  }, [cameraReady, clearCountdownInterval, finalRecommendation, loading, result, startCamera, stopCamera, uiStage]);

  const finalizeQuestionFlow = React.useCallback(async () => {
    if (!result || finalizing || finalRecommendation) return;
    setFinalizing(true);
    setError(null);
    try {
      if (!drinkPreference) throw new Error(t.aiPickNoMatchingDrinks);
      const allMachineProducts = await loadMachineProducts();
      const eligible = productsForPreference(allMachineProducts, drinkPreference);
      if (allMachineProducts.length > 0 && eligible.length === 0) {
        throw new Error(t.aiPickNoMatchingDrinks);
      }
      if (
        allMachineProducts.length === 0 &&
        (!isDemoMode || drinkPreference === "alcoholic")
      ) {
        throw new Error(t.aiPickNoMatchingDrinks);
      }
      const payload = await postFinalizeOrThrow(ML_API_URL, {
        emotion: result.emotion,
        guest_id: result.guest_memory?.guest_id ?? null,
        guest_mode: null,
        lang: getLang(),
        allow_alcoholic: allowAlcoholic,
        ...(eligible.length > 0
          ? { inventory: eligible, machine_menu: [] }
          : { machine_menu: products.map((p) => p.id) }),
        question_answers: [],
      });
      setFinalRecommendation(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Final recommendation failed.");
    } finally {
      setFinalizing(false);
    }
  }, [allowAlcoholic, drinkPreference, finalRecommendation, finalizing, isDemoMode, loadMachineProducts, products, result]);

  React.useEffect(() => {
    if (!result || finalRecommendation || finalizing) return;
    void finalizeQuestionFlow();
  }, [finalRecommendation, finalizing, finalizeQuestionFlow, result]);

  React.useEffect(() => {
    if (!finalRecommendation || uiStage !== "scan") return;
    setUiStage("recommend");
  }, [finalRecommendation, uiStage]);

  const handleUseRecommendation = async (productId: string) => {
    if (!eligibleProductIds.has(productId)) {
      setError(t.aiPickNoMatchingDrinks);
      return;
    }
    const machineProduct = machineProducts.find((m) => m.product_id === productId);
    const product: Product | undefined = machineProduct
      ? {
          id: machineProduct.product_id,
          icon: machineProduct.image_url ?? "",
          name: machineProductName(machineProduct),
          desc: t.productDescs[machineProduct.product_id] ?? "",
          basePrice: (machineProduct.price_cents ?? 0) / 100,
        }
      : products.find((e) => e.id === productId);
    if (!product) { setError(`Recommended product "${productId}" is not in the active menu.`); return; }

    try {
      await fetch(`${ML_API_URL.replace(/\/$/, "")}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guest_id: result?.guest_memory?.guest_id ?? finalRecommendation?.guest_id ?? null,
          guest_mode: null,
          selected_product_id: productId,
          emotion: result?.emotion ?? DEFAULT_FALLBACK_EMOTION,
          context_source: result?.context_source ?? "camera_burst",
          question_answers: [],
        }),
      });
    } catch { /* keep flow moving */ }

    setUiStage("preparing");
    await sleep(PREPARING_DELAY_MS);

    const user = order.user;
    const standalonePourMl = machineProduct
      ? Math.min(
          AI_POUR_ML,
          machineProduct.max_pour_ml ?? AI_POUR_ML,
          machineProduct.quantity_ml,
        )
      : AI_POUR_ML;
    resetOrder();
    updateOrder({
      product,
      totalPrice: product.basePrice,
      orderMode: "ai",
      // A 'mixed' preset pours from its own recipe (no custom mix); a
      // standalone product on a pipe pours a fixed AI serving.
      customMix:
        machineProduct && isMixedProduct(machineProduct)
          ? ({} as CustomMix)
          : ({ ...ZERO_MIX, [productId]: standalonePourMl } as CustomMix),
      user,
    });
    navigate("/qr-order");
  };

  const recommendedChoices = finalRecommendation
    ? (
        finalRecommendation.ranked_recommendations ??
        finalRecommendation.ranked_product_ids.map((id) => ({ product_id: id, reason: "" }))
      )
        .filter((entry) => eligibleProductIds.has(entry.product_id))
        .slice(0, 2)
        .map((entry) => {
          const machineProduct = machineProducts.find(
            (m) => m.product_id === entry.product_id,
          );
          if (machineProduct) {
            const product: Product = {
              id: machineProduct.product_id,
              icon: machineProduct.image_url ?? "",
              name: machineProductName(machineProduct),
              desc: t.productDescs[machineProduct.product_id] ?? "",
              basePrice: (machineProduct.price_cents ?? 0) / 100,
            };
            return { product, reason: entry.reason || product.desc };
          }
          const product = products.find((c) => c.id === entry.product_id);
          if (!product) return null;
          return { product, reason: entry.reason || product.desc };
        })
        .filter((e): e is { product: Product; reason: string } => e !== null)
    : [];

  /** Timer-based fallback when native FaceDetector is unavailable */
  React.useEffect(() => {
    if (uiStage !== "scan" || !cameraReady || faceDetectionSupported !== false || loading || result || countdownStartedRef.current) return undefined;
    const id = window.setTimeout(() => {
      if (!analyzeInFlightRef.current && !loadingRef.current && !resultRef.current && !errorRef.current && !cameraErrorRef.current) {
        armCaptureCountdown();
      }
    }, 6000);
    return () => window.clearTimeout(id);
  }, [cameraReady, faceDetectionSupported, loading, result, armCaptureCountdown, uiStage]);

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col overflow-hidden [animation:pageFadeIn_0.35s_ease_both]">
      <div className="noise-overlay" />
      <Header title={t.aiPickTitle} onBack={handleBack} />

      {(cameraError || error) && (
        <div
          className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-black/85 px-8 backdrop-blur-sm"
          // A camera error (e.g. permission denied) can't be recovered by
          // retrying on this page, so dismissing it returns to the previous
          // screen instead of looping back into the camera. A recommendation
          // error just clears so the guest can try again.
          onClick={() => {
            if (cameraError) { handleBack(); return; }
            setError(null);
          }}
        >
          <div className="font-display text-[clamp(1.4rem,2vw,1.8rem)] font-bold uppercase tracking-[0.16em] text-red-400">
            {t.aiPickErrorTitle}
          </div>
          <div className="max-h-[min(60vh,420px)] max-w-[min(calc(100vw-2rem),520px)] overflow-y-auto text-left text-[clamp(0.88rem,1.35vw,1.05rem)] leading-snug text-[var(--cream)] whitespace-pre-wrap break-words">
            {cameraError || error}
          </div>
          <div className="mt-2 text-[0.82rem] uppercase tracking-[0.14em] text-[var(--muted)]">
            {t.aiPickTapToDismiss}
          </div>
        </div>
      )}

      <div
        className={`relative flex flex-1 min-h-0 flex-col overflow-hidden ${
          uiStage === "scan" ? "px-4 py-4" : ""
        }`}
      >
        {uiStage === "intro" && (
          <BarScene
            messageKey={introCanContinue ? "scan-msg" : "welcome-msg"}
            message={introCanContinue ? t.aiPickIntroScanReady : t.aiPickIntroWelcome}
            tray={
              introCanContinue ? (
                <button
                  type="button"
                  onClick={() => setUiStage("preference")}
                  className="ai-bubble__cta touch-target !mt-0"
                >
                  {t.aiPickContinue}
                </button>
              ) : undefined
            }
          />
        )}

        {uiStage === "preference" && (
          <BarScene
            messageKey="drink-preference-msg"
            message={t.aiPickDrinkPreferencePrompt}
            tray={
              <div className="flex w-full items-stretch justify-center gap-4 px-4">
                {([
                  {
                    value: "alcoholic" as const,
                    label: t.aiPickAlcoholOption,
                    description: t.aiPickAlcoholOptionDesc,
                    icon: "🍸",
                    available: hasAlcoholicProducts,
                    unavailable: machineCatalogLoading
                      ? t.aiPickLoadingDrinkOptions
                      : knownUnderage
                        ? t.aiPickAgeRestricted
                        : t.aiPickOptionUnavailable,
                  },
                  {
                    value: "non-alcoholic" as const,
                    label: t.aiPickAlcoholFreeOption,
                    description: t.aiPickAlcoholFreeOptionDesc,
                    icon: "🌿",
                    available: hasNonAlcoholicProducts,
                    unavailable: machineCatalogLoading
                      ? t.aiPickLoadingDrinkOptions
                      : t.aiPickOptionUnavailable,
                  },
                ]).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={!option.available}
                    onClick={() => {
                      setDrinkPreference(option.value);
                      setUiStage("scan");
                    }}
                    className="touch-target flex w-[min(38vw,300px)] flex-col items-center gap-2 rounded-[20px] border border-[var(--border)] bg-[rgba(7,10,18,0.86)] px-5 py-5 text-center text-[var(--cream)] shadow-[var(--card-shadow)] backdrop-blur-sm transition-all duration-150 enabled:active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <span aria-hidden className="text-[2.4rem] leading-none">{option.icon}</span>
                    <span className="font-display text-[clamp(1.05rem,1.7vw,1.4rem)] font-bold uppercase tracking-[0.08em]">
                      {option.label}
                    </span>
                    <span className="text-[clamp(0.8rem,1.1vw,0.95rem)] leading-snug text-[var(--muted)]">
                      {option.available ? option.description : option.unavailable}
                    </span>
                  </button>
                ))}
              </div>
            }
          />
        )}

        {uiStage === "scan" && (
          <div className="flex flex-1 min-h-0 items-center justify-center overflow-hidden">
            <div
              ref={previewContainerRef}
              className="relative w-full max-h-full overflow-hidden rounded-[24px] border border-[var(--border)] bg-black shadow-[var(--card-shadow)]"
              style={{ aspectRatio: cameraIntrinsic ? `${cameraIntrinsic.w} / ${cameraIntrinsic.h}` : "16 / 9", maxWidth: "100%" }}
            >
              <video ref={videoRef} autoPlay className="absolute inset-0 h-full w-full object-cover" style={{ transform: "scaleX(-1)" }} muted playsInline />
              {countdownSec !== null && (
                <div aria-live="polite" className="pointer-events-none absolute inset-0 z-[40] flex items-center justify-center bg-black/35">
                  <span className="font-display text-[clamp(5rem,28vw,10rem)] font-bold tabular-nums leading-none text-white drop-shadow-[0_6px_24px_rgba(0,0,0,0.85)]">
                    {countdownSec}
                  </span>
                </div>
              )}
              {!cameraReady && !cameraError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[0.95rem] text-[var(--muted)]">{t.aiPickStartingCamera}</span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-[rgba(0,0,0,0.35)] px-3 py-2 text-center text-[0.72rem] leading-snug text-[var(--muted)] uppercase tracking-[0.14em]">
                {!cameraReady ? (
                  t.aiPickStartingCamera
                ) : faceDetectionSupported === null ? (
                  t.aiPickLoadingFaceDetection
                ) : countdownSec !== null ? (
                  <span className="normal-case tracking-normal text-[var(--cream)]">{t.aiPickHoldStillCapturing}</span>
                ) : faceDetectionSupported === false ? (
                  <span className="normal-case tracking-normal text-[var(--cream)]">{t.aiPickStayInFrame}</span>
                ) : faceDetected && holdingStill ? (
                  <span className="normal-case tracking-normal text-[var(--cream)]">{t.aiPickHoldStillAlmostReady}</span>
                ) : faceDetected ? (
                  <span className="normal-case tracking-normal text-[var(--cream)]">{t.aiPickHoldHeadSteady}</span>
                ) : (
                  <span className="normal-case tracking-normal text-[var(--cream)]">{t.aiPickLookAtCamera}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {(uiStage === "recommend" || uiStage === "preparing") && (
          <BarScene
            messageKey={
              uiStage === "preparing"
                ? "preparing-msg"
                : `suggest-msg-${result?.emotion ?? "neutral"}`
            }
            message={(() => {
              if (uiStage === "preparing") return t.aiPickPreparingDrinkForYou;
              const emotionKey = result?.emotion?.toString().trim().toLowerCase();
              const moodLabel = emotionKey ? t.aiPickEmotionLabels[emotionKey] : undefined;
              return moodLabel ? t.aiPickSuggestDrinksForMood(moodLabel) : t.aiPickSuggestDrinks;
            })()}
            tray={
              <>
                {uiStage === "recommend" && recommendedChoices.length > 0 && (
                  <div
                    className="flex w-full flex-col items-center gap-3"
                    // Lift the choices up so the two cards flank the bartender
                    // (one left, one right) rather than sitting low on the counter.
                    style={{ marginBottom: "clamp(20px, 11vh, 130px)" }}
                  >
                    <div className="rounded-full bg-black/55 px-4 py-1.5 text-[0.78rem] uppercase tracking-[0.16em] text-[var(--cream)] backdrop-blur-sm">
                      {t.aiPickPickOneToContinue}
                    </div>
                    {/* Full-width row: card 1 far left, card 2 far right, with the
                        bartender standing in the clear centre between them. */}
                    <div className="flex w-full items-end justify-between gap-2 px-2 sm:gap-4 sm:px-[clamp(6px,3vw,72px)]">
                      {recommendedChoices.map(({ product, reason }, index) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => { void handleUseRecommendation(product.id); }}
                          className={`touch-target flex w-[clamp(130px,42vw,300px)] flex-col items-center gap-2 rounded-[20px] border p-3 text-center backdrop-blur-sm transition-all duration-150 active:scale-[0.97] sm:w-[clamp(190px,25vw,300px)] sm:gap-3 sm:p-4 ${
                            index === 0
                              ? "border-[var(--confirm-border)] bg-[rgba(81,175,47,0.2)] shadow-[0_0_26px_rgba(81,175,47,0.32)]"
                              : "border-[var(--border)] bg-[rgba(7,10,18,0.82)]"
                          }`}
                        >
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] border border-[var(--border)] bg-[var(--card)] sm:h-[4.25rem] sm:w-[4.25rem]">
                            <ProductIcon
                              icon={product.icon}
                              name={product.name}
                              imgClassName="block h-9 w-9 object-contain sm:h-[2.8rem] sm:w-[2.8rem]"
                              emojiClassName="text-4xl leading-none sm:text-[2.8rem]"
                            />
                          </div>
                          <div className="font-display text-[clamp(1rem,1.6vw,1.4rem)] font-bold leading-tight text-[var(--cream)]">
                            {`${index + 1}. ${product.name}`}
                          </div>
                          {reason && (
                            <div
                              className="text-[clamp(0.82rem,1.1vw,0.98rem)] leading-snug text-[var(--muted)]"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {reason}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {uiStage === "recommend" && recommendedChoices.length === 0 && (
                  <div className="rounded-[18px] border border-[var(--border)] bg-[rgba(7,10,18,0.85)] px-5 py-4 text-[0.95rem] text-[var(--cream)] backdrop-blur-sm">
                    {t.aiPickPreparingOptions}
                  </div>
                )}

                {uiStage === "preparing" && (
                  <div className="flex flex-col items-center gap-2">
                    <ShakerLoader size={150} label={t.aiPickPreparingLoader} />
                    <div className="font-display text-[0.95rem] font-bold uppercase tracking-[0.12em] text-[var(--cream)]">
                      {t.aiPickPreparingLabel}
                    </div>
                  </div>
                )}
              </>
            }
          />
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {uiStage === "scan" && (loading || finalizing) && (
        <div className="kiosk-flow-wrap shrink-0 pb-5 pt-3">
          <div className="touch-target flex w-full items-center justify-center rounded-[18px] border border-[var(--border)] bg-[var(--card)] py-5 font-display text-[clamp(1rem,1.4vw,1.2rem)] font-bold uppercase tracking-[0.12em] text-[var(--cream)] opacity-90">
            {loading ? t.aiPickAnalyzingMood : t.aiPickPreparingSuggestions}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPickPage;
